import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Users,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  FileText,
  Sparkles,
  AlertTriangle,
  Minus,
  Plus,
  Pencil,
  Check,
  CheckCircle2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMobileData } from '../MobileDataContext'
import { MTopbar } from '../MobileLayout'
import { ScoreRing } from '../../components/ScoreRing'
import type { SessionGrades } from '../../api/types'

type Tab = 'paper' | 'eval'

type QGrade = SessionGrades[number]['question_grades'][number]

/* Deterministic pseudo AI-confidence (demo data has no real value).
   Stable per question id so the "needs review" set never flickers. */
function aiConfidence(qId: number): number {
  return 0.78 + ((qId * 7) % 22) / 100 // 0.78 – 0.99
}
const REVIEW_THRESHOLD = 0.85

export default function MGradeReview() {
  const { examId, paperId } = useParams()
  const eId = Number(examId)
  const pId = Number(paperId)
  const navigate = useNavigate()
  const { t } = useTranslation('pages')
  const {
    exams,
    className,
    getGrades,
    getPaper,
    pagesFor,
    setQuestionScore,
    setQuestionFeedback,
    approvePaper,
    isPaperApproved,
  } = useMobileData()

  const [tab, setTab] = useState<Tab>('paper')

  const exam = exams.find((e) => e.id === eId)
  const grades = getGrades(eId)
  const paper = getPaper(eId, pId)
  const pages = pagesFor(pId)

  const index = grades.findIndex((g) => g.paper_id === pId)
  const prev = index > 0 ? grades[index - 1] : null
  const next = index >= 0 && index < grades.length - 1 ? grades[index + 1] : null

  const reviewCount = useMemo(
    () =>
      (paper?.question_grades ?? []).filter((q) => aiConfidence(q.id) < REVIEW_THRESHOLD).length,
    [paper],
  )

  if (!paper) {
    return (
      <>
        <MTopbar title={t('mobile.gradeReview.paperFallback')} back backTo={`/m/exams/${eId}/setup`} />
        <div className="m-empty">
          <FileText />
          <div>{t('mobile.gradeReview.paperNotFound')}</div>
        </div>
      </>
    )
  }

  const total = paper.paper_grade?.total_score ?? 0
  const approved = isPaperApproved(pId)
  const go = (target: number) => navigate(`/m/exams/${eId}/review/${target}`)

  return (
    <div className="m-rev">
      <MTopbar title={exam?.session_name ?? t('mobile.gradeReview.examFallback')} subtitle={t('mobile.gradeReview.subtitle')} back backTo={`/m/exams/${eId}/setup`} />

      {/* Student header */}
      <div className="m-rev__head">
        <div className="m-rev__avatar">
          <Users size={22} />
        </div>
        <ScoreRing score={total} maxScore={100} size={48} />
        <div className="m-rev__who">
          <div className="m-rev__name">{paper.student_name}</div>
          <div className="m-rev__sub">
            {className(exam?.class_id)} · No {index + 1}
          </div>
        </div>
        <div className="m-rev__nav">
          <button className="m-rev__navbtn" disabled={!prev} onClick={() => prev && go(prev.paper_id)} aria-label={t('mobile.gradeReview.prevAria')}>
            <ChevronLeft size={18} />
          </button>
          <button className="m-rev__navbtn" disabled={!next} onClick={() => next && go(next.paper_id)} aria-label={t('mobile.gradeReview.nextAria')}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="m-segment">
        <button className={`m-segment__btn${tab === 'paper' ? ' active' : ''}`} onClick={() => setTab('paper')}>
          {t('mobile.gradeReview.tabPaper')}
        </button>
        <button className={`m-segment__btn${tab === 'eval' ? ' active' : ''}`} onClick={() => setTab('eval')}>
          {t('mobile.gradeReview.tabGrade')}
          {reviewCount > 0 && <span className="m-count-badge">{reviewCount}</span>}
        </button>
      </div>

      <div className="m-scroll">
        <div className="m-page">
          {tab === 'paper' ? (
            <PaperTab pages={pages} />
          ) : (
            <EvalTab
              questions={paper.question_grades}
              reviewCount={reviewCount}
              onScore={(gradeId, score) => setQuestionScore(eId, pId, gradeId, score)}
              onFeedback={(gradeId, fb) => setQuestionFeedback(eId, pId, gradeId, fb)}
            />
          )}
        </div>
      </div>

      {/* Sticky approve */}
      <div className="m-actionbar">
        <button
          className={`m-btn m-btn--block m-btn--lg ${approved ? 'm-btn--soft' : 'm-btn--primary'}`}
          onClick={() => approvePaper(eId, pId)}
          disabled={approved}
        >
          {approved ? (
            <>
              <CheckCircle2 size={20} /> {t('mobile.gradeReview.approved')}
            </>
          ) : (
            <>
              <Check size={20} /> {t('mobile.gradeReview.approvePaper')}
            </>
          )}
        </button>
      </div>
    </div>
  )
}

/* ── Paper tab (dark canvas, zoom/rotate, page chips) ─────── */
function PaperTab({ pages }: { pages: { id: string; url: string }[] }) {
  const { t } = useTranslation('pages')
  const [page, setPage] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [rot, setRot] = useState(0)

  const current = pages[page]

  return (
    <div className="m-rev__paper">
      <div className="m-rev__papercanvas">
        {current ? (
          <img
            src={current.url}
            alt={t('mobile.gradeReview.pageAlt', { n: page + 1 })}
            style={{ transform: `scale(${zoom}) rotate(${rot}deg)` }}
          />
        ) : (
          <div className="m-rev__paperph">
            <FileText size={40} />
            <div>{t('mobile.gradeReview.noPaper')}</div>
            <div style={{ fontSize: '0.78rem', color: '#475569' }}>
              {t('mobile.gradeReview.noPaperSub')}
            </div>
          </div>
        )}
      </div>

      <div className="m-rev__papertools">
        <button className="m-rev__tool" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} aria-label={t('mobile.gradeReview.zoomOut')}>
          <ZoomOut size={18} />
        </button>
        <button className="m-rev__tool" onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))} aria-label={t('mobile.gradeReview.zoomIn')}>
          <ZoomIn size={18} />
        </button>
        <button className="m-rev__tool" onClick={() => setRot((r) => (r + 90) % 360)} aria-label={t('mobile.gradeReview.rotate')}>
          <RotateCw size={18} />
        </button>
        <button
          className="m-rev__tool"
          onClick={() => {
            setZoom(1)
            setRot(0)
          }}
          aria-label={t('mobile.gradeReview.reset')}
        >
          <Maximize2 size={18} />
        </button>
      </div>

      {pages.length > 1 && (
        <div className="m-rev__pagechips">
          {pages.map((p, i) => (
            <button
              key={p.id}
              className={`m-rev__pagechip${i === page ? ' active' : ''}`}
              onClick={() => {
                setPage(i)
                setZoom(1)
                setRot(0)
              }}
            >
              {t('mobile.gradeReview.pageChip', { n: i + 1 })}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Evaluation tab (question cards) ──────────────────────── */
function EvalTab({
  questions,
  reviewCount,
  onScore,
  onFeedback,
}: {
  questions: QGrade[]
  reviewCount: number
  onScore: (gradeId: number, score: number) => void
  onFeedback: (gradeId: number, feedback: string) => void
}) {
  const { t } = useTranslation('pages')
  return (
    <div className="m-stack">
      <div className="m-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 2px 4px' }}>
        <Sparkles size={14} /> {t('mobile.gradeReview.aiAssessment')}
      </div>

      {reviewCount > 0 && (
        <div className="m-rev__alert">
          <AlertTriangle size={18} /> {t('mobile.gradeReview.nQuestionsToReview', { count: reviewCount })}
        </div>
      )}

      {questions.map((q) => (
        <QuestionCard
          key={q.id}
          q={q}
          onScore={(score) => onScore(q.id, score)}
          onFeedback={(fb) => onFeedback(q.id, fb)}
        />
      ))}
    </div>
  )
}

function QuestionCard({
  q,
  onScore,
  onFeedback,
}: {
  q: QGrade
  onScore: (score: number) => void
  onFeedback: (feedback: string) => void
}) {
  const { t } = useTranslation('pages')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(q.feedback)

  const conf = Math.round(aiConfidence(q.id) * 100)
  const status =
    q.score >= q.max_score
      ? { label: t('mobile.gradeReview.statusCorrect'), cls: 'm-pill--success' }
      : q.score <= 0
        ? { label: t('mobile.gradeReview.statusWrong'), cls: 'm-pill--danger' }
        : { label: t('mobile.gradeReview.statusPartial'), cls: 'm-pill--warning' }

  const answer = q.student_answer?.trim() || t('mobile.gradeReview.answerNotExtracted')

  return (
    <div className="m-card m-qcard">
      <div className="m-qcard__head">
        <div className="m-qcard__no">
          <i>{q.question_display ?? q.question_number}</i>
          {t('mobile.gradeReview.questionN', { n: q.question_number })}
        </div>
        <span className={`m-pill ${status.cls}`}>{status.label}</span>
      </div>

      <div className="m-qcard__box m-qcard__box--answer">
        <div className="m-qcard__boxlabel">{t('mobile.gradeReview.studentAnswerLabel')}</div>
        {answer}
      </div>

      <div className="m-qcard__box m-qcard__box--ai">
        <div className="m-qcard__boxlabel">
          <Sparkles size={12} /> {t('mobile.gradeReview.aiCommentLabel')}
        </div>
        {q.feedback}
        <div className="m-qcard__conf">
          <Sparkles size={13} /> {t('mobile.gradeReview.aiConfidence', { pct: conf })}
        </div>
      </div>

      {editing ? (
        <>
          <textarea
            className="m-textarea m-textarea--fb"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <div className="m-qcard__foot">
            <button
              className="m-qcard__editfb"
              onClick={() => {
                setDraft(q.feedback)
                setEditing(false)
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              className="m-btn m-btn--soft"
              style={{ height: 38, padding: '0 16px' }}
              onClick={() => {
                onFeedback(draft)
                setEditing(false)
              }}
            >
              <Check size={16} /> {t('common.save')}
            </button>
          </div>
        </>
      ) : (
        <div className="m-qcard__foot">
          <div className="m-stepper">
            <button
              className="m-stepper__btn"
              onClick={() => onScore(q.score - 1)}
              disabled={q.score <= 0}
              aria-label="Azalt"
            >
              <Minus size={18} />
            </button>
            <div className="m-stepper__val">
              {q.score} <small>/ {q.max_score}</small>
            </div>
            <button
              className="m-stepper__btn"
              onClick={() => onScore(q.score + 1)}
              disabled={q.score >= q.max_score}
              aria-label="Artır"
            >
              <Plus size={18} />
            </button>
          </div>
          <button className="m-qcard__editfb" onClick={() => setEditing(true)}>
            <Pencil size={15} /> {t('mobile.gradeReview.editComment')}
          </button>
        </div>
      )}
    </div>
  )
}
